import React, { useEffect, useState, useRef } from "react";
import {
  Form,
  Button,
  Alert,
  Spinner,
  ProgressBar,
  InputGroup,
} from "react-bootstrap";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { userLogin } from "../../features/user/userSlice"; // Adjust path if needed
import { BACKEND_URL } from "constants";
import { EyeFill, EyeSlashFill } from "react-bootstrap-icons";

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const passwordRef = useRef(null);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [sendEnabled, setSendEnabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [lengthError, setLengthError] = useState("");
  const [matchError, setMatchError] = useState("");
  const [passwordHint, setPasswordHint] = useState("");
  const [strengthPercent, setStrengthPercent] = useState(0);
  const [strengthVariant, setStrengthVariant] = useState("danger");

  const [responseMessage, setResponseMessage] = useState("");
  const [responseError, setResponseError] = useState("");

  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [isCooldown, setIsCooldown] = useState(false);

  useEffect(() => {
    axios
      .post(`${BACKEND_URL}/verify-reset-token`, { token })
      .then(() => {
        setTokenValid(true);
        setLoading(false);
        passwordRef.current?.focus();
      })
      .catch((error) => {
        const msg =
          error.response?.data?.message || "Invalid or expired reset link.";
        setResponseError(msg);
        setTokenValid(false);
        setLoading(false);
      });
  }, [token]);

  const validatePassword = (value) => {
    const rules = [
      { regex: /.{8,}/, message: "At least 8 characters." },
      { regex: /[a-zA-Z]/, message: "At least one letter." },
      { regex: /\d/, message: "At least one number." },
    ];

    return rules.filter((rule) => !rule.regex.test(value));
  };

  const calculateStrength = (password) => {
    let percent = 0;
    if (password.length >= 8) percent += 25;
    if (/[a-zA-Z]/.test(password)) percent += 25;
    if (/\d/.test(password)) percent += 25;
    if (/[@$!%*?&]/.test(password)) percent += 25;

    let variant = "danger";
    if (percent >= 75) variant = "success";
    else if (percent >= 50) variant = "warning";

    return { percent, variant };
  };

  const handleNewPasswordChange = (event) => {
    const value = event.target.value;
    setNewPassword(value);

    const failedRules = validatePassword(value);
    setPasswordHint(
      failedRules.length > 0
        ? failedRules.map((r) => r.message).join(" ")
        : "Valid password ✅"
    );

    const strength = calculateStrength(value);
    setStrengthPercent(strength.percent);
    setStrengthVariant(strength.variant);

    if (confirmPassword && value !== confirmPassword) {
      setMatchError("Passwords do not match.");
    } else {
      setMatchError("");
    }

    setSendEnabled(value === confirmPassword && failedRules.length === 0);
    setLengthError(value.length === 0 ? "Password is required." : "");
  };

  const handleConfirmPasswordChange = (event) => {
    const value = event.target.value;
    setConfirmPassword(value);

    if (value !== newPassword) {
      setMatchError("Passwords do not match.");
    } else {
      setMatchError("");
    }

    setSendEnabled(
      value === newPassword && validatePassword(newPassword).length === 0
    );
  };

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handleSubmit = (event) => {
    event.preventDefault();
    setResponseMessage("");
    setResponseError("");
    setIsCooldown(true);
    setTimeout(() => setIsCooldown(false), 3000);

    axios
      .post(`${BACKEND_URL}/update-password`, {
        token,
        password: newPassword,
      })
      .then((response) => {
        setResponseMessage(
          response.data.message || "Password reset successful!"
        );

        // --- AUTO LOGIN LOGIC ---
        if (response.data.user) {
          dispatch(userLogin({ user: response.data.user }));
        }

        setNewPassword("");
        setConfirmPassword("");
        setPasswordHint("");
        setStrengthPercent(0);

        navigate("/reset-confirmation", {
          state: { username: response.data.user?.username },
        });
      })
      .catch((error) => {
        const msg =
          error.response?.data?.message ||
          "Error resetting password. Please try again.";
        setResponseError(msg);
      });
  };

  if (loading) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-card bg-light rounded text-center">
          <Spinner animation="border" role="status" className="mb-3" />
          <p className="mb-0">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-card bg-light rounded">
          <div className="reset-password-header">
            <h2 className="reset-password-title text-danger">
              Invalid or Expired Link
            </h2>
            <p className="reset-password-subtitle">
              This password reset link is no longer valid.
            </p>
          </div>

          <Alert variant="danger" className="reset-password-alert">
            {responseError || "This password reset link is no longer valid."}
          </Alert>

          <p className="reset-password-text">
            The reset link you used is invalid or has expired. To reset your
            password, please request a new link.
          </p>

          <Link to="/forget-password" className="reset-password-link">
            <Button variant="primary" className="reset-password-button mt-2">
              Request New Reset Link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-card bg-light rounded">
        <div className="reset-password-header">
          <h2 className="reset-password-title">Reset Password</h2>
          <p className="reset-password-subtitle">
            Please enter your new password and confirm it below.
          </p>
        </div>

        <Form onSubmit={handleSubmit} className="reset-password-form">
          <Form.Group controlId="formResetPassword" className="mb-3">
            <InputGroup className="mb-2">
              <Form.Control
                ref={passwordRef}
                placeholder="New Password"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={handleNewPasswordChange}
                required
              />
              <Button
                variant="outline-secondary"
                onClick={togglePasswordVisibility}
                type="button"
              >
                {showPassword ? <EyeSlashFill /> : <EyeFill />}
              </Button>
            </InputGroup>

            {lengthError && (
              <small className="text-danger d-block mb-1">{lengthError}</small>
            )}
            {passwordHint && (
              <small
                className={`d-block mb-2 ${
                  passwordHint.includes("Valid password")
                    ? "text-success"
                    : "text-danger"
                }`}
              >
                {passwordHint}
              </small>
            )}

            <ProgressBar
              now={strengthPercent}
              variant={strengthVariant}
              className="mb-3"
              style={{ height: "6px" }}
            />

            <Form.Control
              className="mb-2"
              placeholder="Confirm Password"
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              required
            />

            {matchError && (
              <small className="text-danger d-block">{matchError}</small>
            )}
          </Form.Group>

          {responseMessage && (
            <Alert variant="success" className="reset-password-alert">
              {responseMessage}
            </Alert>
          )}

          {responseError && (
            <Alert variant="danger" className="reset-password-alert">
              {responseError}
            </Alert>
          )}

          <Button
            variant="success"
            type="submit"
            disabled={!sendEnabled || isCooldown}
            className="reset-password-button"
          >
            {isCooldown ? "Please wait..." : "Confirm"}
          </Button>
        </Form>
      </div>
    </div>
  );
};

export default ResetPassword;